import { DatePickerPopover } from "$root/components/date-picker-popover";
import { serverYesterdayAsLocalDate } from "$root/utils";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { useTranslation } from "react-i18next";
import { earliestAsOfDay, parseAsOfParam } from "./asOf";
import type { AsOfSnapshotState } from "./useAsOfSnapshot";

/**
 * Also reachable as the bar's "Return to today" — kept here too so the way back never depends on which
 * trigger opened the picker. Disabled in the live view, where the picker is opened from the header's own
 * "Today" button and returning would clear a date that was never set: greyed rather than hidden, so the
 * footer keeps its shape and the button says there is nowhere to go back to.
 */
function ReturnToTodayButton(props: {
	asOf?: string;
	setAsOf(asOf: string | null): void;
	close(): void;
}): React.JSX.Element {
	const { asOf, setAsOf, close } = props;
	const { t } = useTranslation();
	return (
		<Button
			size="x-small"
			palette="tertiary"
			disabled={asOf == null}
			onClick={() => {
				setAsOf(null);
				close();
			}}
			data-qualifier="PortfolioDetails/AsOfPicker/Today"
		>
			{t("PORTFOLIO_DETAILS.AS_OF_PICKER.RETURN_TO_TODAY")}
		</Button>
	);
}

/** Built outside the component, so the footer's render prop is not a component declared during render. */
function returnToTodayAction(asOf: string | undefined, setAsOf: (asOf: string | null) => void) {
	return (close: () => void) => <ReturnToTodayButton asOf={asOf} setAsOf={setAsOf} close={close} />;
}

/**
 * The as-of date picker popover (MDOT-24860), reached only through `AsOfButton`, so what can be
 * picked cannot drift apart between surfaces.
 *
 * Every day in range is selectable, weekends included: the min is the day after the portfolio's
 * inception and the max is server-yesterday — returning to the live view goes through the "Return to
 * today" action, which clears the date.
 *
 * Picking or typing a day stages it; "Apply" is what pins the page (MDOT-24860), because a date
 * nobody has computed yet costs a multi-minute backend generation. That same button reads
 * "Try again" once a date has failed — the only way to re-ask, since nothing retries the ask on
 * the page's behalf.
 *
 * Everything else the popover does — the masked field, the draft it stages, the blur
 * canonicalisation, the range hint — is `DatePickerPopover`'s, shared with the composition editor's
 * market date picker. What this file supplies is the window, the rule that settles the field, the
 * strings, and the two actions the footer carries.
 */
export function AsOfDatePicker(props: {
	/** Validated as-of date (`YYYY-MM-DD`); unset = live view. */
	asOf?: string;
	setAsOf(asOf: string | null): void;
	/** Whether the pinned date's snapshot can be read yet; absent in the live view. */
	snapshot?: AsOfSnapshotState;
	/** Portfolio inception date (`YYYY-MM-DD` or full ISO timestamp); the day after it is the earliest selectable. */
	inceptionDate?: string;
	/** Rendered as the popover trigger (`asChild`), so it must be able to carry button props and a ref. */
	children: React.JSX.Element;
}): React.JSX.Element {
	const { asOf, setAsOf, snapshot, inceptionDate, children } = props;
	const { t } = useTranslation();

	// Re-applying the date the page is already pinned to is how the user asks again after a failure:
	// nothing retries on the page's behalf now that the ask is an explicit action. It is offered only
	// in the error state, because re-asking a date that is generating or already stored changes nothing.
	const retry = snapshot?.kind === "error" ? snapshot.retry : null;

	return (
		<DatePickerPopover
			value={asOf ?? null}
			min={earliestAsOfDay(inceptionDate)}
			max={serverYesterdayAsLocalDate()}
			// The same rule that vets `?asOf`, so what can be typed and what can be linked to are one set
			// rather than two that drift.
			accept={(day) => parseAsOfParam(day, { inceptionDate })}
			labels={{
				dialog: t("PORTFOLIO_DETAILS.AS_OF_PICKER.LABEL"),
				typeADate: t("PORTFOLIO_DETAILS.AS_OF_PICKER.TYPE_A_DATE"),
				commit: t("PORTFOLIO_DETAILS.AS_OF_PICKER.APPLY"),
				recommit: t("PORTFOLIO_DETAILS.AS_OF_PICKER.RETRY"),
			}}
			rangeHint={({ from, to }) =>
				from
					? t("PORTFOLIO_DETAILS.AS_OF_PICKER.RANGE_HINT", { from, to })
					: t("PORTFOLIO_DETAILS.AS_OF_PICKER.RANGE_HINT_NO_MIN", { to })
			}
			qualifier="PortfolioDetails/AsOfPicker"
			allowRecommit={retry != null}
			// One button, because re-asking for the pinned date *is* applying it again.
			onCommit={(day) => {
				if (retry != null && day === asOf) {
					retry();
				} else {
					setAsOf(day);
				}
			}}
			secondaryAction={returnToTodayAction(asOf, setAsOf)}
		>
			{children}
		</DatePickerPopover>
	);
}
